import { FulfillmentTypes } from '@moda/portal-stanchions';
import { SizeInventoryFragment } from '../../../generated/types';
import { GetDeliveryEstimateQuery } from './deliveryEstimate.rest';

export const SAME_DAY_METHOD_ID = 3;
export const RANGE_COPY = 'Ships in 5 to 7 days';
export const SAME_DAY_AREA_COPY = 'Same-day delivery in Manhattan';
export const SAME_DAY_HOURS_COPY = 'Same-day delivery is available on orders placed by 1pm ET.';

const MS_PER_MINUTE = 60_000;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const STALE_DATE_DAYS = 30;
const MS_PER_HOUR = MS_PER_MINUTE * MINUTES_PER_HOUR;
const STALE_DATE_MS = STALE_DATE_DAYS * HOURS_PER_DAY * MS_PER_HOUR;

export const COUNTDOWN_WINDOW_MS = MS_PER_HOUR;

const FULFILMENT_TYPES_WITH_OWN_MESSAGE: (number | null)[] = [
  FulfillmentTypes.DropShip,
  FulfillmentTypes.MiraklDropship
];

const FULFILMENT_TYPES_WITHOUT_DATES: (number | null)[] = [FulfillmentTypes.MiraklEconcession];

type Estimate = GetDeliveryEstimateQuery['deliveryEstimate'];

export interface DeliveryOption {
  id: number;
  label: string;
  charges: number;
  date: string;
  sameDay: boolean;
}

export type SameDayState = 'today' | 'after-cutoff' | 'check-zip' | 'none';

export type DeliveryView =
  | { kind: 'hidden' }
  | { kind: 'range' }
  | { kind: 'size-ships-later'; shipsBy: string | null }
  | {
      kind: 'options';
      fastest: DeliveryOption;
      others: DeliveryOption[];
      sameDay: SameDayState;
      cutoffAt: string | null;
    };

const shortLabel = (name: string) => name.replace(/\s+(Shipping|Delivery)$/i, '');

const shortDate = (arrivalDate: string) => arrivalDate.replace(/^(\w{3})\w*/, '$1');

const arrivalTime = (arrivalDate: string, now: Date) => {
  const monthDay = arrivalDate.replace(/^\w+,\s*/, '');
  const date = new Date(`${monthDay} ${now.getFullYear()}`);
  if (date.getTime() < now.getTime() - STALE_DATE_MS) date.setFullYear(now.getFullYear() + 1);
  return date.getTime();
};

const sizeShipsNow = (size: SizeInventoryFragment) => (size.available ?? 0) > (size.onOrder ?? 0);

const sameDayOpen = (estimate: Estimate, now: Date) => {
  const cutoffAt = estimate.sameDay?.cutoffAt;
  return Boolean(estimate.sameDay?.available && cutoffAt && new Date(cutoffAt) > now);
};

const sameDayState = (estimate: Estimate, hasZipCode: boolean, now: Date): SameDayState => {
  if (sameDayOpen(estimate, now)) return 'today';
  if (estimate.shippingMethods?.some(method => method.id === SAME_DAY_METHOD_ID)) {
    return 'after-cutoff';
  }
  if (!hasZipCode && estimate.sameDay?.eligible) return 'check-zip';
  return 'none';
};

export const buildDeliveryView = ({
  estimate,
  fulfilmentType,
  size,
  hasZipCode,
  now
}: {
  estimate: Estimate | undefined;
  fulfilmentType: number | null;
  size: SizeInventoryFragment | null;
  hasZipCode: boolean;
  now: Date;
}): DeliveryView => {
  if (FULFILMENT_TYPES_WITH_OWN_MESSAGE.includes(fulfilmentType)) return { kind: 'hidden' };
  if (FULFILMENT_TYPES_WITHOUT_DATES.includes(fulfilmentType)) return { kind: 'range' };

  if (size && !sizeShipsNow(size)) {
    return { kind: 'size-ships-later', shipsBy: size.processDeliveryDates?.deliveryEndsOn ?? null };
  }

  if (!estimate) return { kind: 'hidden' };

  const sameDay = sameDayState(estimate, hasZipCode, now);

  const options = (estimate.shippingMethods ?? [])
    .filter(method => method.id !== SAME_DAY_METHOD_ID || sameDay === 'today')
    .flatMap(method => {
      if (!method.arrivalDate) return [];
      const isSameDay = method.id === SAME_DAY_METHOD_ID;
      return [
        {
          option: {
            id: method.id,
            label: shortLabel(method.name),
            charges: method.charges,
            date: isSameDay ? 'Today' : shortDate(method.arrivalDate),
            sameDay: isSameDay
          },
          time: isSameDay ? 0 : arrivalTime(method.arrivalDate, now)
        }
      ];
    })
    .sort((left, right) => left.time - right.time || left.option.charges - right.option.charges)
    .map(({ option }) => option);

  if (options.length === 0) return { kind: 'range' };

  const [fastest, ...others] = options;

  return {
    kind: 'options',
    fastest,
    others,
    sameDay,
    cutoffAt: sameDay === 'today' ? (estimate.sameDay?.cutoffAt ?? null) : null
  };
};

export const formatCutoff = (cutoffAt: string) =>
  new Date(cutoffAt)
    .toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'America/New_York'
    })
    .replace(':00', '')
    .replace(/\s?(AM|PM)$/, (_match, period: string) => period.toLowerCase());

export const formatCountdown = (msLeft: number) => {
  const totalMinutes = Math.max(1, Math.floor(msLeft / MS_PER_MINUTE));
  const hours = Math.floor(totalMinutes / MINUTES_PER_HOUR);
  const minutes = totalMinutes % MINUTES_PER_HOUR;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
};
